import type { CSSProperties } from "react";
import { avatarGradient, avatarInitials } from "./avatar-identity";

/**
 * Avatar de lead, contato ou perfil.
 *
 * Sem foto, mostra as iniciais sobre um gradiente derivado do nome, como no
 * protótipo: a mesma pessoa tem sempre a mesma cor, o que ajuda a reconhecer a
 * linha sem ler. A cor é decorativa; nome, etapa e prioridade continuam em
 * texto (AGENTS §11).
 *
 * A imagem é decorativa porque o nome aparece ao lado em todas as superfícies;
 * quando não aparecer, quem usa passa `alt` explícito.
 */
export interface AvatarProps {
  name: string;
  photoUrl?: string;
  size?: "default" | "lg";
  /** Texto alternativo quando o avatar é a única identificação visível. */
  alt?: string;
  className?: string;
}

export function Avatar({ name, photoUrl, size = "default", alt, className }: AvatarProps) {
  return (
    <span
      className={className ? `cx-avatar ${className}` : "cx-avatar"}
      data-size={size === "lg" ? "lg" : undefined}
      style={photoUrl ? undefined : ({ "--cx-avatar-bg": avatarGradient(name) } as CSSProperties)}
    >
      {photoUrl ? (
        <img src={photoUrl} alt={alt ?? ""} aria-hidden={alt ? undefined : "true"} />
      ) : (
        <>
          <span aria-hidden="true">{avatarInitials(name)}</span>
          {alt && <span className="cx-sr-only">{alt}</span>}
        </>
      )}
    </span>
  );
}
